import {
  FORUM_FEEDBACK_HEADER_TRUNCATE_LENGTH,
  Highlight,
} from '@modules/feedback/types';
import checkDisabledIndent from '@modules/feedback/utils/checkDisabledIndent';
import { trackCopyFeedbackContentEvents } from '@modules/feedback/utils/trackCopyFeedbackContentEvents';
import { ArrowDownwardRounded } from '@mui/icons-material';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import { Button, Fab, IconButton, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import scrollIntoView from 'scroll-into-view-if-needed';
import {
  Feedback,
  FeedbackDetailsConversationVariant,
} from 'v2/core/entities/feedback';
import { FeedbackContentForum } from 'v2/core/entities/feedback/content';
import ForumMessagePreviewTag from './ForumMessagePreviewTag';
import Header from './ForumMessageTypeHeader';
import LastMessage from './ForumMessageTypeLastMessage';
import PreviousMessages from './ForumMessageTypePreviousMessages';

interface ForumMessageTypeProps {
  feedback: Feedback;
  highlights: {
    [key: string]: Highlight[];
  };
  showOriginalLanguage: boolean;
  variant: FeedbackDetailsConversationVariant;
}

const ForumMessageType = ({
  feedback,
  highlights,
  showOriginalLanguage,
  variant,
}: ForumMessageTypeProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const lastMsgRef = useRef<HTMLDivElement>(null);

  const content = showOriginalLanguage
    ? (feedback.content as FeedbackContentForum)?.forumContentOriginal
    : (feedback.content as FeedbackContentForum)?.forumContentEn;

  const sourceTitle = content?.title;

  const msgIndexForLastMessage =
    (content?.isHeaderPresent ? 1 : 0) + (content?.parentMsgs?.length || 0);

  // NOTE: Highlights in Discourse.
  // In Discourse cards, the highlights contain the index of the message. So,
  // we have to figure out if the message contains highlights. We do that by
  // setting the index of each message.
  //
  // For Header, it's always 0. But, the Header is optional. We need to check
  // the `isHeaderPresent` boolean attribute to check if the message contains a Header.
  //
  // For the PreviousMessages, the index can start from 1 as the message
  // containing PreviousMessages will always contain the Header.
  //
  // For the LastMessage, the index can start after the Header (optional) and
  // PreviousMessages (optional). That's why we have passed the `msgIndex` prop
  // to the LastMessage component.

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isPreview = variant === 'preview';

  const isHeaderTextTruncated =
    (content?.header?.text?.length || 0) >
    FORUM_FEEDBACK_HEADER_TRUNCATE_LENGTH.Default;

  // Defines if the conversation has more than 3 messages including the header
  // Why 3? - This is the max number of messages that we show in collapsed state.
  const containsMoreThanMaxMessages = msgIndexForLastMessage > 2;

  const shouldShowExpandBtn =
    isCollapsed &&
    !isPreview &&
    (containsMoreThanMaxMessages || isHeaderTextTruncated);

  const shouldDisableIndent = checkDisabledIndent(feedback);

  const parentContainerId = `feedback-preview-${feedback.id}`;

  const handleScrollToLastMessage = () => {
    if (lastMsgRef.current) {
      scrollIntoView(lastMsgRef.current, {
        block: 'end',
        // We need to add a boundary so that the scroll of the page doesn't
        // get affected.
        boundary: document.getElementById(parentContainerId),
        behavior: 'smooth',
      });
    }
  };

  // Hide FAB when Last Message is in view
  const intersection = useIntersection(lastMsgRef, {
    root: null,
    threshold: 0.5,
  });

  const shouldShowFAB = !isCollapsed && !intersection?.isIntersecting;

  const hasParentMsgs = Boolean(content?.parentMsgs?.length);

  const lastMessageProps = {
    showOriginalLanguage,
    content,
    isCollapsed,
    highlights,
    msgIndex: msgIndexForLastMessage,
    sourceTitle,
    variant,
    isIndentationDisabled: shouldDisableIndent || isPreview,
    ref: lastMsgRef,
  };

  return (
    <Box
      onCopy={() => {
        trackCopyFeedbackContentEvents({
          eventName: 'Copied Text From Feedback Content',
          feedbackID: feedback.id,
          feedbackType: feedback.content.type,
          contentType: 'raw',
        });
      }}
    >
      <Stack
        borderRadius={2}
        sx={{
          overflow: 'auto',
        }}
      >
        {content?.isHeaderPresent && (
          <>
            <Box
              sx={{
                position: 'relative',
                backgroundColor: 'feedback.chatBubble.secondary',
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                paddingBottom: 2,
                marginBottom: isCollapsed ? 2 : 0,
              }}
            >
              {isPreview && <ForumMessagePreviewTag title="Parent Thread" />}
              <Header
                showOriginalLanguage={showOriginalLanguage}
                content={content}
                highlights={highlights}
                isCollapsed={isCollapsed}
                sourceTitle={sourceTitle}
                variant={variant}
              />
              {!isCollapsed && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 24,
                  }}
                  onClick={toggleCollapse}
                >
                  <UnfoldLessRoundedIcon />
                </IconButton>
              )}
            </Box>
            {shouldShowExpandBtn && (
              <Button
                sx={{
                  width: 'max-content',
                  mb: hasParentMsgs ? 0 : 2,
                }}
                onClick={toggleCollapse}
              >
                View{' '}
                {isHeaderTextTruncated && !containsMoreThanMaxMessages
                  ? 'More'
                  : 'All Comments'}
              </Button>
            )}
            {!isPreview && (
              <Stack overflow={'hidden'}>
                <PreviousMessages
                  showOriginalLanguage={showOriginalLanguage}
                  content={content}
                  highlights={highlights}
                  shouldDisableIndent={shouldDisableIndent}
                  isCollapsed={isCollapsed}
                />
                <LastMessage {...lastMessageProps} />
              </Stack>
            )}
          </>
        )}
        {(!content?.isHeaderPresent || isPreview) && (
          <LastMessage {...lastMessageProps} />
        )}
      </Stack>
      {shouldShowFAB && (
        <Box
          position="sticky"
          bottom={0}
          display="flex"
          justifyContent="flex-end"
        >
          <Fab
            color="primary"
            variant="extended"
            onClick={handleScrollToLastMessage}
          >
            <ArrowDownwardRounded />
            Highlighted comment
          </Fab>
        </Box>
      )}
    </Box>
  );
};

export default ForumMessageType;
